import React, { useEffect, useReducer, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link, RouteComponentProps } from 'react-router-dom'
import { App, Breadcrumb, Modal, Tooltip } from 'antd'
import {
  InfoCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import jwtDecode from 'jwt-decode'
import { useManualQuery } from 'graphql-hooks'
import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import uniq from 'lodash/uniq'
import { v4 as uuidv4 } from 'uuid'

import Api from '@vacationtracker/shared/services/api'
import * as logger from '../../services/logger'
import { initialState, reducer } from './reducer'
import { actions } from './actions'
import { filterData } from './filter-data'
import { useShouldEnableFeatures } from '../../store/use-should-enable-features'
import { getSyncedCalendarsData } from '../../graphql/custom-queries'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { track } from '../../services/analytics/analytics'
import { wait } from '@vacationtracker/shared/functions/wait'
import { GoogleAuth } from '../../services/auth/google/google'
import { MicrosoftAuth } from '../../services/auth/microsoft/microsoft'

import IntlMessages from '../../util/IntlMessages'
import CircularProgress from '../../components/circular-progress'
import FilterAdvanced from '@vacationtracker/shared/components/filter-advanced'
import {
  Calendars,
  Connect,
  CreateUpdate,
  Empty,
  EmptySearch,
  IcalCreate,
  IcalSummary,
  ConnectCalendarAction,
  helpDeskLink,
  Reconnect,
  OutlookAdminConsentModal
} from './components'

import { ISyncedCalendarsData } from '../../types/custom-queries'
import { IGoogleCalendar, IOutlookCalendar, ISyncedCalendar, ISyncedCalendarTypeEnum } from '@vacationtracker/shared/types/calendar'
import { ISelected } from '@vacationtracker/shared/components/filter-advanced/types'
import { FrontendUrls } from '../../types/urls'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { IFilter } from '@vacationtracker/shared/types/filter'
import { CalendarSyncViewEnum, ICalendarSyncState, CalendarSyncStorageEnum} from './types'
import { ITokens as IMicrosoftTokens } from '../../types/microsoft'
import { IGoogleOAuthTokens } from '@vacationtracker/shared/types/google'
import { CalendarAlreadyConnected } from '@vacationtracker/shared/errors/calendar'


const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID)
const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID as string)


const CalendarSyncPage: React.FC<RouteComponentProps> = (props) => {
  const { formatMessage } = useIntl()
  const { modal, notification } = App.useApp()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const shouldEnableFeatures = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)
  const shouldEnableSyncedSharedCalendar = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.syncSharedCalendars)
  const [calendarTokens, setCalendarTokens] = useState<IMicrosoftTokens | IGoogleOAuthTokens | null>(null)
  const [state, dispatch] = useReducer(
    reducer,
    initialState
  ) as [ICalendarSyncState, React.Dispatch<any>]
  const {
    calendars,
    filters,
    isFetchingData,
    whatToRender,
    calendar,
    calendarToConnect,
    googleSyncCalendarData,
    outlookSyncCalendarData,
    locations,
    departments,
    labels,
    initialFilterValues,
    calendarToConnectType,
    filteredCalendars,
    calendarForAdminConsent,
    isGrantAdminConsentModalOpen,
    showMsLoginRequiredModal,
    calendarOwnerEmail,
    userWhoConnectedCalendar,
    isReconnecting,
  } = state
  const {
    setCalendarToConnect,
    setCalendarToConnectType,
    setGoogleSyncCalendarData,
    setOutlookSyncCalendarData,
    setWhatToRender,
    setInitialFilters,
    setFilteredCalendars,
    setFilters,
    getCalendarsStart,
    getCalendarsSuccess,
    setCalendar,
    toggleGrantAdminConsentModal,
    toggleMsLoginModal,
    setOutlookCalendarOwnerAndUserWhoConnectedCalendar,
    setCalendarForAdminConsent,
    setIsReconnecting,
    setPushPublicHolidays,
  } = actions
  const timeoutAbortController = new AbortController()
  const activePollingRef = useRef<Set<string>>(new Set())

  const handleInitialFilters = (shouldReset: boolean) => {
    const [ locationIds = [], teamIds = [], labelIds = [] ] = ['locations', 'departments', 'labels'].map(name => new URLSearchParams(location.search).get(name)?.split(','))
    const initialUrlFilters: ISelected[] = []
    if (locationIds?.length) {
      initialUrlFilters.push({ type: 'Locations', values: locationIds })
    }
    if (teamIds?.length) {
      initialUrlFilters.push({ type: 'Departments', values: teamIds })
    }
    if (labelIds?.length) {
      initialUrlFilters.push({ type: 'Labels', values: labelIds })
    }

    const initilFiltersCalendarStateInit = localStorage.getItem(`${authUser?.id ? `${authUser?.id}#` : ''}calendarFilters`)
    const initilFiltersCalendarState = initilFiltersCalendarStateInit ? JSON.parse(initilFiltersCalendarStateInit) : [{}]
    if (shouldReset) {
      if (initilFiltersCalendarState) {
        dispatch(setInitialFilters([{}]))
        localStorage.removeItem(`${authUser?.id ? `${authUser?.id}#` : ''}calendarFilters`)
      }
      return
    }
    const initilFilters = initilFiltersCalendarState?.length !== 0 && !isEmpty(initilFiltersCalendarState[0])
      ? initilFiltersCalendarState
      : initialUrlFilters.length > 0
        ? initialUrlFilters
        : [{}]
    dispatch(setInitialFilters(initilFilters))
  }

  const handleUrlFilterParams = (data) => {
    const { locationIds, teamIds, labelIds } = data
    let url = location.pathname
    let searchParams = ''
    const calendarIdParam = new URLSearchParams(location.search).get('calendarId')
    if (calendarIdParam) {
      searchParams += `&calendarId=${calendarIdParam}`
    }
    if (locationIds?.length) {
      searchParams += `&locations=${locationIds.join(',')}`
    }
    if (teamIds?.length) {
      searchParams += `&departments=${teamIds.join(',')}`
    }
    if (labelIds?.length) {
      searchParams += `&labels=${labelIds.join(',')}`
    }
    url += searchParams.replace('&', '?')
    window.history.pushState({}, '', url)
  }

  useEffect(() => {
    handleInitialFilters(false)
    return () => {
      // Cancel timeouts in the `wait` function
      timeoutAbortController.abort()
    }
  }, [])

  useEffect(() => {
    const googleSyncCalendar = localStorage.getItem(CalendarSyncStorageEnum.googleSyncCalendar)
    if (googleSyncCalendar) {
      dispatch(setGoogleSyncCalendarData(JSON.parse(googleSyncCalendar)))
    }
  }, [])

  useEffect(() => {
    const calendarsData: ISyncedCalendar[] = calendars
    if (isEmpty(filters.teamIds) && isEmpty(filters.locationIds) && isEmpty(filters.labelIds)) {
      dispatch(setFilteredCalendars(calendarsData))
    }
    const filteredCalendars: ISyncedCalendar[] = filterData(filters, calendarsData)
    dispatch(setFilteredCalendars(filteredCalendars))
  }, [filters])

  useEffect(() => {
    fetchCalendars()
  }, [])

  useEffect(() => {
    // ROUTING
    switch (props.location.pathname) {
      case FrontendUrls.calendarSyncPage:
        dispatch(setWhatToRender(CalendarSyncViewEnum.calendars))
        break
      case FrontendUrls.calendarSyncPageIcalCreate:
        dispatch(setWhatToRender(CalendarSyncViewEnum.icalCreate))
        break
      case FrontendUrls.calendarSyncPageIcalSummary: {
        dispatch(setWhatToRender(CalendarSyncViewEnum.icalSummary))
        const calendarId = new URLSearchParams(location.search).get('calendarId') && new URLSearchParams(location.search).get('calendarId')
        const foundCalendar = calendars.find(calendar => calendar.id === calendarId)
        dispatch(setCalendar(foundCalendar))
        break
      }
      case FrontendUrls.calendarSyncPageGoogleReconnect: {
        const calendarId = new URLSearchParams(props.location.search).get('calendarId')
        const calendarToReconnect = find(state.calendars, (cal: ISyncedCalendar) => cal.id === calendarId)
        dispatch(setCalendarToConnect(calendarToReconnect))
        dispatch(setWhatToRender(CalendarSyncViewEnum.googleReconnect))
        break
      }
      case FrontendUrls.calendarSyncPageOutlookReconnect: {
        const calendarId = new URLSearchParams(props.location.search).get('calendarId')
        const calendarToReconnect = find(state.calendars, (cal: ISyncedCalendar) => cal.id === calendarId)
        dispatch(setCalendarToConnect(calendarToReconnect))
        dispatch(setWhatToRender(CalendarSyncViewEnum.outlookReconnect))
        break
      }
      case FrontendUrls.calendarSyncPageGoogleConnect: {
        const googleSyncCalendar = localStorage.getItem(CalendarSyncStorageEnum.googleSyncCalendar)
        if (googleSyncCalendar) {
          props.history.push(FrontendUrls.calendarSyncPageGoogleCreate)
          dispatch(setWhatToRender(CalendarSyncViewEnum.googleCreate))
          dispatch(setGoogleSyncCalendarData(JSON.parse(googleSyncCalendar)))
          break
        } else {
          dispatch(setWhatToRender(CalendarSyncViewEnum.googleConnect))
          break
        }
      }
      case FrontendUrls.calendarSyncPageGoogleCreate: {
        const googleSyncCalendar = localStorage.getItem(CalendarSyncStorageEnum.googleSyncCalendar)
        if (googleSyncCalendar) {
          dispatch(setWhatToRender(CalendarSyncViewEnum.googleCreate))
          dispatch(setGoogleSyncCalendarData(JSON.parse(googleSyncCalendar)))
          break
        } else {
          props.history.push(FrontendUrls.calendarSyncPageGoogleConnect)
          dispatch(setWhatToRender(CalendarSyncViewEnum.googleConnect))
          break
        }
      }
      case FrontendUrls.calendarSyncPageGoogleUpdate: {
        const calendarId = new URLSearchParams(location.search).get('calendarId') && new URLSearchParams(location.search).get('calendarId')
        const foundCalendar = calendars.find(calendar => calendar.id === calendarId)
        dispatch(setCalendar(foundCalendar))
        const initialFilters: ISelected[] = []
        if (foundCalendar?.locations?.length) {
          initialFilters.push({ type: 'Locations', values: foundCalendar?.locations })
        }
        if (foundCalendar?.departments?.length) {
          initialFilters.push({ type: 'Departments', values: foundCalendar?.departments })
        }
        if (foundCalendar?.labels?.length) {
          initialFilters.push({ type: 'Labels', values: foundCalendar?.labels })
        }
        dispatch(setInitialFilters(initialFilters))
        dispatch(setWhatToRender(CalendarSyncViewEnum.googleUpdate))
        break
      }
      case FrontendUrls.calendarSyncPageOutlookConnect: {
        const outlookSyncCalendar = localStorage.getItem(CalendarSyncStorageEnum.outlookSyncCalendar)
        if (outlookSyncCalendar) {
          props.history.push(FrontendUrls.calendarSyncPageOutlookCreate)
          dispatch(setWhatToRender(CalendarSyncViewEnum.outlookCreate))
          dispatch(setOutlookSyncCalendarData(JSON.parse(outlookSyncCalendar)))
          break
        } else {
          dispatch(setWhatToRender(CalendarSyncViewEnum.outlookConnect))
          break
        }
      }
      case FrontendUrls.calendarSyncPageOutlookCreate: {
        const outlookSyncCalendar = localStorage.getItem(CalendarSyncStorageEnum.outlookSyncCalendar)
        if (outlookSyncCalendar) {
          dispatch(setWhatToRender(CalendarSyncViewEnum.outlookCreate))
          dispatch(setOutlookSyncCalendarData(JSON.parse(outlookSyncCalendar)))
          break
        } else {
          props.history.push(FrontendUrls.calendarSyncPageOutlookConnect)
          dispatch(setWhatToRender(CalendarSyncViewEnum.outlookConnect))
          break
        }
      }
      case FrontendUrls.calendarSyncPageOutlookUpdate: {
        const calendarId = new URLSearchParams(location.search).get('calendarId') && new URLSearchParams(location.search).get('calendarId')
        const foundCalendar = calendars.find(calendar => calendar.id === calendarId)
        dispatch(setCalendar(foundCalendar))
        const initialFilters: ISelected[] = []
        if (foundCalendar?.locations?.length) {
          initialFilters.push({ type: 'Locations', values: foundCalendar?.locations })
        }
        if (foundCalendar?.departments?.length) {
          initialFilters.push({ type: 'Departments', values: foundCalendar?.departments })
        }
        if (foundCalendar?.labels?.length) {
          initialFilters.push({ type: 'Labels', values: foundCalendar?.labels })
        }
        dispatch(setInitialFilters(initialFilters))
        dispatch(setWhatToRender(CalendarSyncViewEnum.outlookUpdate))
        break
      }
      default:
        dispatch(setWhatToRender(CalendarSyncViewEnum.calendars))
        break
    }
  }, [props.location.pathname, calendars])

  const [ getSyncedCalendarsDataQuery ] = useManualQuery<ISyncedCalendarsData, { companyId: string }>(getSyncedCalendarsData)

  const fetchCalendars = async () => {
    try {
      if (!authCompany?.id) {
        return
      }
      dispatch(getCalendarsStart())
      const response = await getSyncedCalendarsDataQuery({ variables: { companyId: authCompany?.id }})
      if (!response.data || response.error) throw response.error
      dispatch(getCalendarsSuccess({
        locations: response.data?.getLocationListV2,
        departments: response.data?.getTeamListV2,
        labels: response.data?.getLabelListV2,
        calendars: [
          ...response.data?.getCalendars?.filter(cal => !cal.deleted).map((calendar: ISyncedCalendar) => {
            return {
              type: !calendar.type || calendar.type === ISyncedCalendarTypeEnum.ical ? ISyncedCalendarTypeEnum.ical : calendar.type,
              id: calendar.id,
              refreshToken: calendar.refreshToken,
              accessToken: calendar.accessToken,
              locations: (calendar.locationIds?.length
                ? uniq([...calendar.locationIds, calendar.locationId]).filter(Boolean)
                : calendar.locationId
                  ? [calendar.locationId]
                  : []) as string[],
              departments: (calendar.teamIds?.length
                ? uniq([...calendar.teamIds, calendar.teamId]).filter(Boolean)
                : calendar.teamId
                  ? [calendar.teamId]
                  : []) as string[],
              labels: calendar.labelIds?.length
                ? calendar.labelIds
                : [],
              creator: calendar.creator,
              user: calendar?.user,
              error: calendar.error || calendar.errors,
              calendarName: calendar.calendarName,
              timezone: calendar.timezone,
              tenant: calendar.tenant,
              pushPublicHolidays: calendar.pushPublicHolidays,
            }
          }) || [],
        ],
      }))
      if (isGrantAdminConsentModalOpen) {
        dispatch(toggleGrantAdminConsentModal())
      }
    } catch (err) {
      logger.error('error fetching calendars', err)
    }
  }

  /**
   * Polls fetchCalendars until the specified calendar appears in the list.
   * This ensures the calendar is visible immediately after creation without requiring a page reload.
   *
   * Features:
   * - Exponential backoff to reduce backend load
   * - AbortController support for cleanup on unmount
   * - Visual feedback via notification updates
   * - Prevents duplicate polling for the same calendar
   *
   * @param calendarId - The ID of the calendar to wait for
   * @param notificationKey - Optional notification key to update during polling
   * @param abortSignal - Optional AbortSignal to cancel polling
   * @param maxWaitTimeMs - Maximum time to wait in milliseconds (default: 30000 = 30 seconds)
   * @param initialPollIntervalMs - Initial interval between polls in milliseconds (default: 500)
   * @param maxPollIntervalMs - Maximum interval between polls in milliseconds (default: 2000)
   * @param checkErrorCleared - If true, also checks that calendar error is cleared (for reconnect scenarios)
   * @returns Promise that resolves when calendar is found or rejects on timeout/abort
   */
  const waitForCalendarToAppear = async (
    calendarId: string,
    notificationKey?: string,
    abortSignal?: AbortSignal,
    maxWaitTimeMs = 30000,
    initialPollIntervalMs = 500,
    maxPollIntervalMs = 2000,
    checkErrorCleared = false
  ): Promise<void> => {
    // Prevent duplicate polling for the same calendar
    if (activePollingRef.current.has(calendarId)) {
      logger.info(`Already polling for calendar ${calendarId}, skipping`)
      return
    }

    activePollingRef.current.add(calendarId)
    const startTime = Date.now()
    let pollInterval = initialPollIntervalMs
    let consecutiveErrors = 0
    const maxConsecutiveErrors = 3

    try {
      while (Date.now() - startTime < maxWaitTimeMs) {
        // Check if aborted
        if (abortSignal?.aborted) {
          throw new Error('Polling aborted')
        }

        try {
          // Guard: ensure companyId is available
          if (!authCompany?.id) {
            throw new Error('Company ID not available')
          }

          // Fetch calendars and check if the calendar appears
          const response = await getSyncedCalendarsDataQuery({ variables: { companyId: authCompany.id }})

          if (response.data?.getCalendars) {
            const foundCalendar = response.data.getCalendars.find(
              (cal: ISyncedCalendar) => cal.id === calendarId && !cal.deleted
            )

            if (foundCalendar) {
              // Calendar found - check if error needs to be cleared (for reconnect)
              if (checkErrorCleared) {
                const hasError = foundCalendar.error || foundCalendar.errors
                if (hasError) {
                  // Calendar exists but still has error, continue polling
                  consecutiveErrors = 0
                } else {
                  // Calendar found and error cleared - update state and return
                  await fetchCalendars()
                  consecutiveErrors = 0
                  return
                }
              } else {
                // Calendar found - update state and return
                await fetchCalendars()
                consecutiveErrors = 0
                return
              }
            }
          }

          // Reset error count on successful query (even if calendar not found yet)
          consecutiveErrors = 0
        } catch (error) {
          consecutiveErrors++
          logger.warning(`Error checking for calendar during polling (attempt ${consecutiveErrors}/${maxConsecutiveErrors}):`, error)

          // Stop polling after too many consecutive errors
          if (consecutiveErrors >= maxConsecutiveErrors) {
            logger.error(`Stopping polling for calendar ${calendarId} due to ${maxConsecutiveErrors} consecutive errors`)
            throw new Error(`Failed to check for calendar after ${maxConsecutiveErrors} attempts`)
          }
        }

        // Update notification with visual feedback
        if (notificationKey) {
          const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000)
          const waitingMessage = formatMessage(
            { id: 'calendar.sync.waitingForCalendar' },
            { seconds: elapsedSeconds }
          )
          const mainMessage = checkErrorCleared
            ? formatMessage({ id: 'calendar.sync.reconnecting' })
            : formatMessage({ id: 'calendar.sync.creating' })
          notification.info({
            key: notificationKey,
            message: mainMessage,
            description: waitingMessage,
            duration: 0, // Keep notification visible
          })
        }

        // Check if aborted before waiting
        if (abortSignal?.aborted) {
          throw new Error('Polling aborted')
        }

        // Wait before next poll with exponential backoff
        await wait(pollInterval, timeoutAbortController)

        // Increase interval exponentially, capped at maxPollIntervalMs
        pollInterval = Math.min(pollInterval * 1.5, maxPollIntervalMs)
      }

      // Timeout reached - still refresh calendars to show what we have
      await fetchCalendars()
      logger.warning(`Calendar ${calendarId} did not appear within ${maxWaitTimeMs}ms`)
    } finally {
      // Always remove from active polling set
      activePollingRef.current.delete(calendarId)
    }
  }

  const filterChanged = (data: IFilter) => {
    if (!isEqual(data, filters)) {
      handleUrlFilterParams(data)
      handleInitialFilters(false)
      dispatch(setFilters(data))
    }
  }

  const handleSelectCalendarToSyncType = (type) => {
    dispatch(setCalendarToConnectType(type.key))
    switch (type.key) {
      case ISyncedCalendarTypeEnum.google:
        props.history.push(FrontendUrls.calendarSyncPageGoogleConnect)
        break
      case ISyncedCalendarTypeEnum.outlook:
        props.history.push(FrontendUrls.calendarSyncPageOutlookConnect)
        break
      case ISyncedCalendarTypeEnum.ical:
        props.history.push(FrontendUrls.calendarSyncPageIcalCreate)
        break
    }
  }

  const handleSelectCalendarToSync = (calendarId: string) => {
    const syncCalendarData = googleSyncCalendarData || outlookSyncCalendarData
    if (!syncCalendarData) return

    const calendar = find(syncCalendarData.calendarList, (cal: IGoogleCalendar | IOutlookCalendar) => cal.id === calendarId) as IGoogleCalendar | IOutlookCalendar
    if (!calendar) return

    if (calendars.find(connectedCalendars => connectedCalendars.id === calendar.id)) {
      notification.error({
        message: formatMessage({ id: 'calendar.sync.calendarAlreadyConnected' }),
      })
      return
    }
    notification.destroy()
    dispatch(setCalendarToConnect(calendar))
  }

  const handleCreateIcalCalendarSync = async () => {
    const notificationKey = uuidv4()
    try {
      notification.info({
        message: formatMessage({ id: 'calendar.sync.creating' }),
        key: notificationKey,
      })
      const response = await Api.post('/core/calendar-id', {
        ...filters,
        creator: authUser?.id,
        type: ISyncedCalendarTypeEnum.ical,
      })
      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creatingSuccess' }),
      })
      await fetchCalendars()
      const url = `${FrontendUrls.calendarSyncPageIcalSummary}?calendarId=${response.id}`
      handleInitialFilters(true)
      props.history.push(url)
      track('CALENDAR_LINK_GENERATED_ICAL', {
        creator: authUser.id,
        companyId: authCompany?.id,
        ...filters,
      })
    } catch (error) {
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creatingError' }),
      })
      logger.warning(error)
    }
  }

  const confirmDeleteCalendar = (calendar: ISyncedCalendar, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation()
    modal.confirm({
      title: formatMessage({ id: 'calendar.sync.deleteTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({
        id: 'calendar.sync.confirmDelete'},
      {
        name: calendar.calendarName,
        strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
      }),
      okText: formatMessage({ id: 'app.delete' }),
      okType: 'danger',
      maskClosable: true,
      onOk() {
        handleDeleteCalendar(calendar.id)
      },
    })
  }

  const handleDeleteCalendar = async (calendarId: string) => {
    const notificationKey = uuidv4()
    try {
      notification.info({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.deleting' }),
      })
      await Api.post('/core/delete-calendar-id', {
        calendarId,
      })
      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.deletingSuccess' }),
      })
      await fetchCalendars()
      handleInitialFilters(true)
      props.history.push(FrontendUrls.calendarSyncPage)
    } catch (error) {
      logger.error(error)
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.deletingError' }),
      })
    }
  }

  const refreshCalendarList = async (platform: 'Google' | 'Outlook') => {
    if (platform === 'Google') {
      if (calendarTokens?.accessToken) {
        await loadGoogleCalendarData(calendarTokens as IGoogleOAuthTokens)
      } else {
        await handleGoogleConnect()
      }
    } else if (platform === 'Outlook') {
      if (calendarTokens?.accessToken) {
        await loadOutlookCalendarData(calendarTokens as IMicrosoftTokens)
      } else {
        await handleOutlookConnect()
      }
    }
  }

  const setGoogleSyncCalendarState = (data) => {
    localStorage.setItem(CalendarSyncStorageEnum.googleSyncCalendar, JSON.stringify(data))
  }

  const loadGoogleCalendarData = async (tokens: IGoogleOAuthTokens) => {
    const googleCalendarsResponse = await googleAuth.getGoogleCalendars(tokens.accessToken as string)
    if (googleCalendarsResponse?.items?.length as number > 0) {
      const userCalendars = filter(googleCalendarsResponse?.items, (cal: IGoogleCalendar) => cal.accessRole === 'owner' || cal.accessRole === 'writer')
      const newGoogleSyncCalendarState = {
        ...tokens,
        calendarList: userCalendars,
      }
      setGoogleSyncCalendarState(newGoogleSyncCalendarState)
      dispatch(setGoogleSyncCalendarData(newGoogleSyncCalendarState))
    }
  }

  const handleGoogleConnect = async () => {
    try {
      const response = await googleAuth.grantCalendarPermission()
      if (response && response.accessToken) {
        setCalendarTokens(response)
        await loadGoogleCalendarData(response)
      }
      if (props.location.pathname !== FrontendUrls.calendarSyncPageGoogleCreate) {
        props.history.push(FrontendUrls.calendarSyncPageGoogleCreate)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  const handleGoogleCreate = async () => {
    const notificationKey = uuidv4()
    try {
      notification.info({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creating' }),
      })
      const body = {
        type: ISyncedCalendarTypeEnum.google,
        accessToken: googleSyncCalendarData?.accessToken,
        refreshToken: googleSyncCalendarData?.refreshToken,
        calendarId: calendarToConnect?.id,
        calendarName: calendarToConnect?.summary,
        timezone: calendarToConnect?.timeZone,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        labelIds: filters.labelIds,
        creator: authUser?.id,
        pushPublicHolidays: calendarToConnect?.pushPublicHolidays,
      }

      await Api.post('/core/calendar-id', body)
      const createdCalendarId = calendarToConnect?.id

      track('SHARED_CALENDAR_GENERATED_GOOGLE', {
        creator: authUser.id,
        companyId: authCompany?.id,
        ...filters,
      })
      localStorage.removeItem(CalendarSyncStorageEnum.googleSyncCalendar)
      dispatch(setCalendarToConnect(null))

      // Wait for calendar to appear in the list before navigating
      if (createdCalendarId) {
        try {
          await waitForCalendarToAppear(
            createdCalendarId,
            notificationKey,
            timeoutAbortController.signal
          )
        } catch (error) {
          logger.warning('Error waiting for calendar to appear', error)
          // Continue anyway - calendar might still appear
        }
      }

      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creatingSuccess' }),
      })
      handleInitialFilters(true)
      props.history.push(FrontendUrls.calendarSyncPage)
    } catch (error) {
      if (error?.response?.data?.code === CalendarAlreadyConnected.code) {
        notification.error({
          key: notificationKey,
          message: formatMessage({ id: 'calendar.sync.calendarAlreadyConnected' }),
          duration: 10,
        })
        return
      }
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creatingError' }),
      })
      logger.error(error)
    }
  }

  const handleGoogleUpdate = async () => {
    const notificationKey = uuidv4()
    try {
      notification.info({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.updating' }),
      })
      const body = {
        type: calendar?.type,
        accessToken: calendar?.accessToken,
        refreshToken: calendar?.refreshToken,
        calendarId: calendar?.id,
        calendarName: calendar?.calendarName,
        timezone: calendar?.timezone,
        creator: state?.calendar?.creator,
        isCalendarUpdate: true,
        // only filters can be changed on calendar update
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        labelIds: filters.labelIds,
        pushPublicHolidays: calendar?.pushPublicHolidays,
      }

      await Api.post('/core/calendar-id', body)
      const updatedCalendarId = calendar?.id

      dispatch(setCalendarToConnect(null))

      // Wait for calendar updates to appear before navigating
      if (updatedCalendarId) {
        try {
          await waitForCalendarToAppear(
            updatedCalendarId,
            notificationKey,
            timeoutAbortController.signal
          )
        } catch (error) {
          logger.warning('Error waiting for calendar updates to appear', error)
          // Continue anyway - calendar might still update
        }
      }

      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.updatingSuccess' }),
      })
      handleInitialFilters(true)
      props.history.push(FrontendUrls.calendarSyncPage)
    } catch (error) {
      logger.error(error)
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.updatingError' }),
      })
    }
  }

  const setOutlookSyncCalendarState = (data) => {
    localStorage.setItem(CalendarSyncStorageEnum.outlookSyncCalendar, JSON.stringify(data))
  }

  const loadOutlookCalendarData = async (tokenResponse: IMicrosoftTokens) => {
    const timezone = await msAuth.getUserOutlookTimezone(tokenResponse.accessToken)
    if (timezone) {
      const outlookCalendarsResponse = await msAuth.getCalendars(tokenResponse.accessToken)
      if (outlookCalendarsResponse?.length as number > 0) {
        const userCalendars = filter(outlookCalendarsResponse, (cal: IOutlookCalendar) => !cal.isDefaultCalendar && (cal.canEdit || cal.canShare))
        const newOutlookSyncCalendarState = {
          ...tokenResponse,
          calendarList: userCalendars.map(cal => ({...cal, timezone })),
        }
        setOutlookSyncCalendarState(newOutlookSyncCalendarState)
        dispatch(setOutlookSyncCalendarData(newOutlookSyncCalendarState))
      }
    }
  }

  const handleOutlookConnect = async () => {
    try {
      const tokenResponse = await msAuth.getWebAppTokensForOutlook('offline_access Calendars.ReadWrite MailboxSettings.Read')
      if (tokenResponse && tokenResponse.accessToken) {
        setCalendarTokens(tokenResponse)
        await loadOutlookCalendarData(tokenResponse)
        if (props.location.pathname !== FrontendUrls.calendarSyncPageOutlookCreate) {
          props.history.push(FrontendUrls.calendarSyncPageOutlookCreate)
        }
      }
    } catch (error) {
      logger.error(error)
    }
  }

  const handleOutlookCreate = async () => {
    let tenantId
    let userWhoConnectedCalendar
    try {
      const decoded = jwtDecode<{tid: string; oid: string}>(outlookSyncCalendarData?.accessToken as string)
      tenantId = decoded.tid
      userWhoConnectedCalendar = decoded.oid
    } catch (error) {
      logger.warning('Error decoding microsoft token - fallback to "common" tenant', error)
      tenantId = 'common'
    }
    const notificationKey = uuidv4()
    try {
      notification.info({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creating' }),
      })
      const body = {
        type: ISyncedCalendarTypeEnum.outlook,
        accessToken: outlookSyncCalendarData?.accessToken,
        refreshToken: outlookSyncCalendarData?.refreshToken,
        calendarId: calendarToConnect?.id,
        calendarName: calendarToConnect?.name,
        timezone: calendarToConnect?.timezone,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        labelIds: filters.labelIds,
        creator: authUser?.id,
        tenant: tenantId,
        calendarOwnerEmail: calendarToConnect?.owner?.address,
        userWhoConnectedCalendar,
        pushPublicHolidays: calendarToConnect?.pushPublicHolidays,
      }

      await Api.post('/core/calendar-id', body)
      const createdCalendarId = calendarToConnect?.id

      track('SHARED_CALENDAR_GENERATED_OUTLOOK', {
        creator: authUser.id,
        companyId: authCompany?.id,
        ...filters,
      })
      localStorage.removeItem(CalendarSyncStorageEnum.outlookSyncCalendar)
      dispatch(setCalendarToConnect(null))

      // Wait for calendar to appear in the list before navigating
      if (createdCalendarId) {
        try {
          await waitForCalendarToAppear(
            createdCalendarId,
            notificationKey,
            timeoutAbortController.signal
          )
        } catch (error) {
          logger.warning('Error waiting for calendar to appear', error)
          // Continue anyway - calendar might still appear
        }
      }

      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creatingSuccess' }),
      })
      handleInitialFilters(true)
      props.history.push(FrontendUrls.calendarSyncPage)
    } catch (error) {
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.creatingError' }),
      // TODO: IVAN add error message from backend
      })
      logger.error(error)
    }
  }

  const handleOutlookReconnect = async (withAdminConsent = false, calendarOwnerEmail?: string, userWhoConnectedCalendar?: string) => {
    dispatch(toggleMsLoginModal(false))
    let tenantId = withAdminConsent ? state.calendarForAdminConsent?.tenant : undefined
    let timezone
    let tokenResponse
    // regular reconnect
    if (!withAdminConsent) {
      tokenResponse = await msAuth.getWebAppTokensForOutlook('offline_access Calendars.ReadWrite MailboxSettings.Read')
      timezone = await msAuth.getUserOutlookTimezone(tokenResponse.accessToken)
      if (!calendarOwnerEmail || !calendarToConnect?.calendarOwnerEmail || calendarForAdminConsent?.calendarOwnerEmail) {
        const outlookCalendar = await msAuth.getCalendar(tokenResponse?.accessToken, calendarToConnect.id)
        calendarOwnerEmail = outlookCalendar?.owner?.address as string
      }
      try {
        const decoded = jwtDecode<{tid: string; oid: string}>(tokenResponse.accessToken as string)
        tenantId = decoded.tid
        userWhoConnectedCalendar = decoded.oid
      } catch (error) {
        logger.warning('Error decoding microsoft token - fallback to "common" tenant', error)
        tenantId = 'common'
      }
    }
    const calendarId = withAdminConsent ? calendarForAdminConsent?.id : calendarToConnect?.id
    const calendarName = withAdminConsent ? calendarForAdminConsent?.calendarName : calendarToConnect?.name
    const notificationKey = uuidv4()
    try {
      notification.info({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.reconnecting' }),
      })
      const body = {
        type: ISyncedCalendarTypeEnum.outlook,
        accessToken: tokenResponse?.accessToken,
        refreshToken: tokenResponse?.refreshToken,
        calendarId,
        calendarName,
        timezone: timezone,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        labelIds: filters.labelIds,
        creator: authUser?.id,
        tenant: tenantId,
        shouldReconnect: !withAdminConsent,
        withAdminConsent,
        userWhoConnectedCalendar,
        calendarOwnerEmail,
      }

      await Api.post('/core/calendar-id', body)
      const reconnectedCalendarId = calendarId

      track('SHARED_CALENDAR_RECONNECTED_OUTLOOK', {
        creator: authUser.id,
        companyId: authCompany?.id,
        ...filters,
      })
      dispatch(setCalendarToConnect(null))

      // Wait for calendar error to clear before navigating
      if (reconnectedCalendarId) {
        try {
          await waitForCalendarToAppear(
            reconnectedCalendarId,
            notificationKey,
            timeoutAbortController.signal,
            30000,
            500,
            2000,
            true // checkErrorCleared = true for reconnect
          )
        } catch (error) {
          logger.warning('Error waiting for calendar error to clear', error)
          // Continue anyway - calendar might still update
        }
      }

      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.reconnectingSuccess' }),
      })
      handleInitialFilters(true)
      dispatch(setIsReconnecting(false))
      props.history.push(FrontendUrls.calendarSyncPage)
    } catch (error) {
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.reconnectingError' }),
      })
      logger.error(error)
    }
  }

  const handleGoogleReconnect = async () => {
    const response = await googleAuth.grantCalendarPermission()
    const notificationKey = uuidv4()
    try {
      notification.info({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.reconnecting' }),
      })
      const body = {
        type: ISyncedCalendarTypeEnum.google,
        accessToken: response?.accessToken,
        refreshToken: response?.refreshToken,
        calendarId: calendarToConnect?.id,
        calendarName: calendarToConnect?.summary,
        timezone: calendarToConnect?.timeZone,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        labelIds: filters.labelIds,
        creator: authUser?.id,
        shouldReconnect: true,
      }

      await Api.post('/core/calendar-id', body)
      const reconnectedCalendarId = calendarToConnect?.id

      track('SHARED_CALENDAR_RECONNECTED_GOOGLE', {
        creator: authUser.id,
        companyId: authCompany?.id,
        ...filters,
      })
      dispatch(setCalendarToConnect(null))

      // Wait for calendar error to clear before navigating
      if (reconnectedCalendarId) {
        try {
          await waitForCalendarToAppear(
            reconnectedCalendarId,
            notificationKey,
            timeoutAbortController.signal,
            30000,
            500,
            2000,
            true // checkErrorCleared = true for reconnect
          )
        } catch (error) {
          logger.warning('Error waiting for calendar error to clear', error)
          // Continue anyway - calendar might still update
        }
      }

      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.reconnectingSuccess' }),
      })
      handleInitialFilters(true)
      props.history.push(FrontendUrls.calendarSyncPage)
    } catch (error) {
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.reconnectingError' }),
      })
      logger.error(error)
    }
  }

  const handleOutlookUpdate = async () => {
    const notificationKey = uuidv4()
    try {
      notification.info({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.updating' }),
      })
      const body = {
        type: calendar?.type,
        accessToken: calendar?.accessToken,
        refreshToken: calendar?.refreshToken,
        calendarId: calendar?.id,
        calendarName: calendar?.calendarName,
        timezone: calendar?.timezone,
        creator: state?.calendar?.creator,
        isCalendarUpdate: true,
        locationIds: filters.locationIds,
        teamIds: filters.teamIds,
        labelIds: filters.labelIds,
        pushPublicHolidays: calendar?.pushPublicHolidays,
      }

      await Api.post('/core/calendar-id', body)
      const updatedCalendarId = calendar?.id

      dispatch(setCalendarToConnect(null))

      // Wait for calendar updates to appear before navigating
      if (updatedCalendarId) {
        try {
          await waitForCalendarToAppear(
            updatedCalendarId,
            notificationKey,
            timeoutAbortController.signal
          )
        } catch (error) {
          logger.warning('Error waiting for calendar updates to appear', error)
          // Continue anyway - calendar might still update
        }
      }

      notification.success({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.updatingSuccess' }),
      })
      handleInitialFilters(true)
      props.history.push(FrontendUrls.calendarSyncPage)
    } catch (error) {
      logger.error(error)
      notification.error({
        key: notificationKey,
        message: formatMessage({ id: 'calendar.sync.updatingError' }),
      })
    }
  }

  const handleCancel = () => {
    handleInitialFilters(true)
    dispatch(setCalendarToConnect(null))
    localStorage.removeItem(CalendarSyncStorageEnum.googleSyncCalendar)
    localStorage.removeItem(CalendarSyncStorageEnum.outlookSyncCalendar)
    props.history.push(FrontendUrls.calendarSyncPage)
  }

  const handleLoginWihDifferentAccount = (provider: 'Google' | 'Outlook') => {
    const localStorageKey = provider === 'Google' ? CalendarSyncStorageEnum.googleSyncCalendar : CalendarSyncStorageEnum.outlookSyncCalendar
    const connectUrl = provider === 'Google' ? FrontendUrls.calendarSyncPageGoogleConnect : FrontendUrls.calendarSyncPageOutlookConnect
    localStorage.removeItem(localStorageKey)
    dispatch(setCalendarToConnect(null))
    props.history.push(connectUrl)
  }

  const handleGrantAdminConsentOpenModalClick = (calendar: ISyncedCalendar) => {
    dispatch(setCalendarForAdminConsent(calendar))
    dispatch(toggleMsLoginModal(true))
  }

  const outlookAdminConsenttHandler = async () => {
    await msAuth.getAdminConsent(calendarForAdminConsent?.tenant as string)
    dispatch(setIsReconnecting(true))
    await handleOutlookReconnect(true, calendarOwnerEmail as string, userWhoConnectedCalendar as string)
  }

  const outlookReconnectHandler = async () => {
    dispatch(setIsReconnecting(true))
    const {calendarOwnerEmail, userWhoConnectedCalendar} = await getCalendarOwner()
    await handleOutlookReconnect(true, calendarOwnerEmail as string, userWhoConnectedCalendar as string)
  }

  const getCalendarOwner = async () => {
    const tokenResponse = await msAuth.getWebAppTokensForOutlook('offline_access Calendars.ReadWrite MailboxSettings.Read')
    let userWhoConnectedCalendar
    const outlookCalendar = await msAuth.getCalendar(tokenResponse?.accessToken as string, calendarForAdminConsent?.id as string)
    const calendarOwnerEmail = outlookCalendar?.owner?.address
    try {
      const decoded = jwtDecode<{tid: string; oid: string}>(tokenResponse.accessToken as string)
      userWhoConnectedCalendar = decoded.oid
    } catch (error) {
      logger.warning('Error decoding microsoft token getCalendarOwner', error)
    }
    return {
      calendarOwnerEmail,
      userWhoConnectedCalendar,
    }
  }

  const getOutlookMsToken = async () => {
    // get basic outlook tokens to extract owner and user who connected calendar
    const {calendarOwnerEmail, userWhoConnectedCalendar} = await getCalendarOwner()
    dispatch(setOutlookCalendarOwnerAndUserWhoConnectedCalendar({
      calendarOwnerEmail: calendarOwnerEmail as string,
      userWhoConnectedCalendar,
    }))
    dispatch(toggleMsLoginModal(false))
    dispatch(toggleGrantAdminConsentModal())
  }

  return (
    <div className="main-content vt-callendar-sync-page">
      <div className="main-content-header">
        <div className="main-content-header-title">
          <IntlMessages id="calendar.sync.title" />&nbsp;
          <Tooltip
            className="info-tooltip"
            title={<IntlMessages
              id="calendar.sync.tooltipInfo"
              values={{
                helpDesk: (...chunks) => (
                  <a
                    href={helpDeskLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />} >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
        <div className="main-content-header-breadcrumb">
          <Breadcrumb
            items={[
              {
                title: <Link to={FrontendUrls.dashboard}><IntlMessages id="sidebar.dashboard" /></Link>,
              },
              {
                title: <Link to={FrontendUrls.calendarPage}><IntlMessages id="sidebar.calendar" /></Link>,
              },
              {
                title: <IntlMessages id="app.sync" />,
              },
            ]}
          />
        </div>
      </div>
      <div className="main-content-body">
        {whatToRender === CalendarSyncViewEnum.calendars && <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <ConnectCalendarAction
            handleSelectCalendarToSyncType={handleSelectCalendarToSyncType}
            shouldEnableSyncedSharedCalendar={shouldEnableSyncedSharedCalendar}
          />
          <div className="filters">
            <div>
              {!isFetchingData &&
                <FilterAdvanced
                  page='calendar'
                  data={{
                    Locations: locations,
                    Departments: departments,
                    Labels: labels,
                  }}
                  onChangeFilter={filterChanged}
                  initialValues={initialFilterValues}
                  showLabels={shouldEnableFeatures}
                  currentUserId={authUser.id}
                />
              }
            </div>
          </div>
        </div>}
        {isFetchingData && <CircularProgress />}
        <Calendars
          state={state}
          confirmDeleteCalendar={confirmDeleteCalendar}
          history={props.history}
          onGrantAdminConsentClick={(calendar: ISyncedCalendar) => handleGrantAdminConsentOpenModalClick(calendar)}
        />
        <Empty
          state={{
            whatToRender,
            isFetchingData,
            calendars: calendars as ISyncedCalendar[],
            calendarToConnectType,
          }}
          shouldEnableSyncedSharedCalendar={shouldEnableSyncedSharedCalendar}
          handleSelectCalendarToSyncType={handleSelectCalendarToSyncType}
        />
        <EmptySearch
          state={{
            whatToRender,
            isFetchingData,
            filteredCalendars,
            calendars: calendars as ISyncedCalendar[],
          }} />
        <IcalCreate
          handleCreateIcalCalendarSync={handleCreateIcalCalendarSync}
          state={{
            whatToRender,
            isFetchingData,
            locations,
            departments,
            labels,
            initialFilterValues,
          }}
          filterChanged={filterChanged}
          handleCancel={handleCancel} />
        <IcalSummary
          state={{
            calendar,
            labels,
            locations,
            departments,
            whatToRender,
          }}
          confirmDeleteCalendar={confirmDeleteCalendar}
          handleCancel={handleCancel} />
        <Connect
          provider={whatToRender === CalendarSyncViewEnum.googleConnect ? 'Google' : 'Outlook'}
          whatToRender={whatToRender}
          handleGoogleConnect={handleGoogleConnect}
          handleOutlookConnect={handleOutlookConnect}
          handleCancel={handleCancel} />
        <Reconnect
          provider={whatToRender === CalendarSyncViewEnum.googleReconnect ? 'Google' : 'Outlook'}
          whatToRender={whatToRender}
          handleGoogleReconnect={handleGoogleReconnect}
          handleOutlookReconnect={() => handleOutlookReconnect(false)}
          handleCancel={handleCancel} />
        <CreateUpdate
          provider={whatToRender === CalendarSyncViewEnum.googleCreate
            || whatToRender === CalendarSyncViewEnum.googleUpdate
            ? 'Google'
            : 'Outlook'}
          state={state}
          handleCancel={handleCancel}
          handleLoginWihDifferentAccount={handleLoginWihDifferentAccount}
          handleGoogleCreate={handleGoogleCreate}
          handleGoogleUpdate={handleGoogleUpdate}
          handleOutlookCreate={handleOutlookCreate}
          handleOutlookUpdate={handleOutlookUpdate}
          handleSelectCalendarToSync={handleSelectCalendarToSync}
          filterChanged={filterChanged}
          confirmDeleteCalendar={confirmDeleteCalendar}
          refreshCalendarList={refreshCalendarList}
          setPushPublicHolidays={(value) => dispatch(setPushPublicHolidays(value))}
        />
        <OutlookAdminConsentModal
          formatMessage={formatMessage}
          calendarName={calendarForAdminConsent?.calendarName as string}
          showModal={isGrantAdminConsentModalOpen}
          tenant={calendarForAdminConsent?.tenant as string}
          clientId={process.env.REACT_APP_MICROSOFT_CLIENT_ID as string}
          handleCancelOutlookAdminConsentModal={() => dispatch(toggleGrantAdminConsentModal())}
          handleOutlookAdminConsent={outlookAdminConsenttHandler}
          handleReconnectAndSync={outlookReconnectHandler}
          isReconnecting={isReconnecting}
        />
        {showMsLoginRequiredModal &&
          <Modal
            title={formatMessage({ id: 'error.microsoft.notificationsLoginRequiredTitle' })}
            open={showMsLoginRequiredModal}
            onOk={getOutlookMsToken}
            onCancel={() => dispatch(toggleMsLoginModal(false))}
          >
            <p><IntlMessages id="calendar.sync.msLogin" /></p>
          </Modal>
        }
      </div>
    </div>
  )
}

export default CalendarSyncPage
